import { formatIstDateTime } from './istTime';
export function parseDateOnly(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Plain date inputs (YYYY-MM-DD) — keep literal, no timezone conversion
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return {
        y: Number(match[1]),
        m: Number(match[2]),
        d: Number(match[3]),
      };
    }
    // Datetime strings from API — use local calendar date
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return {
        y: parsed.getFullYear(),
        m: parsed.getMonth() + 1,
        d: parsed.getDate(),
      };
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
  };
}

export function toInputDate(value) {
  const parts = parseDateOnly(value);
  if (!parts) return '';
  return `${parts.y}-${String(parts.m).padStart(2, '0')}-${String(parts.d).padStart(2, '0')}`;
}

/** datetime-local input value (YYYY-MM-DDTHH:mm) from API/date — no UTC shift. */
export function toInputDateTime(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (match) {
      const [, y, m, d, h = '00', min = '00'] = match;
      return `${y}-${m}-${d}T${h}:${min}`;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return toInputDate(value);
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${min}`;
}

/** Send datetime-local picker value to API without UTC conversion. */
export function toApiDateTimeLocal(value) {
  if (value == null || value === '') return null;
  const trimmed = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const parsed = toInputDateTime(trimmed);
  if (!parsed) return null;
  return parsed.includes(':') && parsed.length > 16 ? `${parsed}:00` : `${parsed}T00:00:00`;
}

export function toDisplayDate(value) {
  const parts = parseDateOnly(value);
  if (!parts) return '';
  return `${String(parts.d).padStart(2, '0')}-${String(parts.m).padStart(2, '0')}-${parts.y}`;
}

/** Dashboard table: "30 Jun 2026" */
export function toShortDisplayDate(value) {
  const parts = parseDateOnly(value);
  if (!parts) return '';
  const d = new Date(parts.y, parts.m - 1, parts.d);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Inclusive list of YYYY-MM-DD strings from fromDate to toDate. */
export function iterateDatesInRange(fromDate, toDate) {
  const start = parseDateOnly(fromDate);
  const end = parseDateOnly(toDate);
  if (!start || !end) return [];

  const dates = [];
  const cur = new Date(start.y, start.m - 1, start.d);
  const endDt = new Date(end.y, end.m - 1, end.d);

  while (cur <= endDt) {
    dates.push(toInputDate(cur));
    cur.setDate(cur.getDate() + 1);
  }

  return dates;
}

export function toCardDate(value) {
  return toDisplayDateTime(value);
}

export function toDisplayDateTime(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (match) {
      const [, y, mo, d, h, min] = match;
      const datePart = `${d}-${mo}-${y}`;
      if (h == null || min == null) return datePart;
      const hour = Number(h);
      const minute = Number(min);
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const timePart = `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
      return `${datePart} ${timePart}`;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return toDisplayDate(value);
  const datePart = toDisplayDate(value);
  const hasTime = date.getHours() !== 0
    || date.getMinutes() !== 0
    || (typeof value === 'string' && value.includes('T'));
  if (!hasTime) return datePart;
  return `${datePart} ${formatIstDateTime(value).split(', ').slice(1).join(', ')}`;
}

/** True when due date/time is before now (local). */
export function isDueDateOverdue(value) {
  if (value == null || value === '') return false;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const parts = parseDateOnly(value);
    if (!parts) return false;
    const endOfDay = new Date(parts.y, parts.m - 1, parts.d, 23, 59, 59, 999);
    return Date.now() > endOfDay.getTime();
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() > date.getTime();
}
